import type { FlowGraphV1 } from '../flowGraph'

export type PreStartPhase = {
  phaseId: string
  label: string
  checkboxCount: number
  filledCount: number
}

type Deps = {
  deckGroups: Array<{ id?: string; cardIds: string[] }>
  allCardIds: string[]
  initialValues: Record<string, boolean[]>
  countCheckboxes: (cardId: string) => number
}

/** Phases in breadth-first order from the start node, with the groups each one enters. */
function collectPhases(graph: FlowGraphV1) {
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]))
  const outgoing = new Map<string, string[]>()
  for (const edge of graph.edges) {
    const list = outgoing.get(edge.fromNodeId) ?? []
    list.push(edge.toNodeId)
    outgoing.set(edge.fromNodeId, list)
  }
  const start = graph.nodes.find((n) => n.type === 'start')
  if (!start) return []

  const phases: Array<{ phaseId: string; label: string; groupIds: string[] }> = []
  let current: (typeof phases)[number] | null = null
  const visited = new Set<string>()
  const queue: string[] = [start.id]
  while (queue.length > 0) {
    const nodeId = queue.shift()!
    if (visited.has(nodeId)) continue
    visited.add(nodeId)
    const node = nodeById.get(nodeId)
    if (!node) continue
    if (node.type === 'phase') {
      const phaseId =
        typeof node.data?.phaseId === 'string' ? (node.data.phaseId as string).trim() : node.id
      current = { phaseId, label: node.label, groupIds: [] }
      phases.push(current)
    } else if (node.type === 'group' && current !== null) {
      const groupId =
        typeof node.data?.groupId === 'string' ? (node.data.groupId as string).trim() : ''
      if (groupId) current.groupIds.push(groupId)
    }
    for (const next of outgoing.get(nodeId) ?? []) if (!visited.has(next)) queue.push(next)
  }
  return phases
}

/**
 * Progress bars shown before a flow starts: one row per phase, one segment per
 * checkbox; a segment is filled when every card of the phase has that many ticks.
 */
export function buildPreStartPhases(graph: FlowGraphV1, deps: Deps): PreStartPhase[] {
  const groupsById = new Map(deps.deckGroups.map((group) => [group.id ?? '', group]))
  return collectPhases(graph).map((phase) => {
    const cardIds: string[] = []
    for (const groupId of phase.groupIds) {
      if (groupId === '__all__') cardIds.push(...deps.allCardIds)
      else cardIds.push(...(groupsById.get(groupId)?.cardIds ?? []))
    }

    let checkboxCount = 1
    for (const id of cardIds) checkboxCount = Math.max(checkboxCount, deps.countCheckboxes(id))

    let filledCount = checkboxCount
    if (cardIds.length === 0) {
      filledCount = 0
    } else {
      for (const id of cardIds) {
        const values = deps.initialValues[id]
        const ticked = Array.isArray(values) ? values.filter(Boolean).length : 0
        if (ticked < filledCount) filledCount = ticked
        if (filledCount === 0) break
      }
    }
    return { phaseId: phase.phaseId, label: phase.label, checkboxCount, filledCount }
  })
}

// Multi-stop gradient: dark blue → blue → turquoise → light orange → dark orange
const GRADIENT: Array<[number, number, number]> = [
  [26, 76, 118],
  [30, 130, 184],
  [76, 184, 204],
  [245, 166, 35],
  [232, 120, 20],
]
const EMPTY_SEGMENT_COLOR = 'rgb(224, 211, 188)'

const lerpGradient = (t: number): string => {
  const n = GRADIENT.length - 1
  const pos = Math.max(0, Math.min(1, t)) * n
  const i = Math.min(Math.floor(pos), n - 1)
  const f = pos - i
  const c1 = GRADIENT[i]!
  const c2 = GRADIENT[Math.min(i + 1, n)]!
  const mix = (k: 0 | 1 | 2) => Math.round(c1[k] + (c2[k] - c1[k]) * f)
  return `rgb(${mix(0)},${mix(1)},${mix(2)})`
}

/** Segment colours of a phase bar: filled segments follow the gradient, the rest are empty. */
export function phaseSegmentColors(phase: PreStartPhase): string[] {
  const n = Math.max(1, phase.checkboxCount)
  const k = Math.max(0, Math.min(n, phase.filledCount))
  return Array.from({ length: n }, (_, i) =>
    i < k ? lerpGradient(n > 1 ? i / (n - 1) : 0) : EMPTY_SEGMENT_COLOR,
  )
}
